import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpUtilsService } from './http-utils.service';
import { environment } from '../../../environment/environment';
import { catchError, map, mergeMap, Observable } from 'rxjs';
import { JsonConvert } from 'json2typescript';
import {
	Session,
	SessionListData,
} from '../../model/troubleshooting/session/session';
import { Paging } from '../../model/paging/paging';
import { LogListData } from '../../model/troubleshooting/log/log';

@Injectable({
	providedIn: 'root',
})
export class TroubleShootingService {
	private _url: string = `${environment.baseAPI}/troubleshooting`;

	constructor(
		private _http: HttpClient,
		private _httpUtils: HttpUtilsService,
	) {}

	/**
   * Retrieves the paginated list of sessions for a specific location and time range. The endpoint supports filtering by location and time range (since/ until, if until is the current date the real time sessions are included).
   *
   * @param location_id - (string, required) The ID of the location
   * @param since - (string, required)The start date for filtering sessions .
   * @param until - (string, optional) The end date for filtering sessions,  If omitted or if the value is the current date, real time sessions are included.
   * @param paging - (Paging, required) The paging information for the request.
   * @param mac_address -(string,optional) The MAC address of the device, If provided, only sessions related to the specified MAC address will be returned.
   * @param ip_address (string, optional): IP address of the device. If provided, only sessions related to the specified IP address will be returned.
   * @param username (string, optional): Username (account radius). If provided, only sessions related to the specified username will be returned.

   * @param field (string, optional): Field to sort by (default: start)
   * @param direction (string, optional): Sort direction. Direction values (default: DESC)
   * @returns An Observable that emits the list of sessions or an error.
   * @throws Throws an error if an exception occurs during the API request.
   */
	GETSessions(
		paging: Paging,
		location_id: string,
		since: string,
		until?: string,
		mac_address?: string,
		ip_address?: string,
		username?: string,
	): Observable<Session | any> {
		try {
			return this._httpUtils.getHTTPHeadersAsyncTenantLocation().pipe(
				mergeMap((httpHeaders) => {
					return this._http
						.get(
							`${this._url}/sessions${paging.getQs()}&location_id=${location_id}&since=${since}${until ? `&until=${until}` : ''}${mac_address ? `&mac_address=${mac_address}` : ''}${ip_address ? `&ip_address=${ip_address}` : ''}${username ? `&username=${username}` : ''}`,
							{ headers: httpHeaders },
						)
						.pipe(
							map(
								(res: any) => {
									const jsonConvert: JsonConvert = new JsonConvert();
									const response: SessionListData =
										jsonConvert.deserializeObject(res, SessionListData);
									if (response.success) {
										return response;
									} else {
										this._httpUtils.handleError(response);
										return;
									}
								},
								catchError((err) => {
									if (err.status === 401) {
										// Unauthorized
										this._httpUtils.logout();
									}
									throw err;
								}),
							),
						);
				}),
				catchError((err) => {
					throw err;
				}),
			);
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Retrieves the paginated list of logs for a specific location and time range ordered by time. The endpoint supports filtering by location and time range (since/ until, if until is the current date the real time logs are included). The endpoint supports also filtering by user mac address and account radius username.
	 *
	 * @param location_id - (string, required) The ID of the location
	 * @param since - The start date for filtering logs .
	 * @param until - (string, optional) The end date for filtering logs,  If omitted or if the value is the current date, real time logs are included.
	 * @param mac_address -(string,optional) The MAC address of the device, If provided, only logs related to the specified MAC address will be returned.
	 * @param username (string, optional): Username (account radius). If provided, only logs related to the specified username will be returned.
	 * @param paging - (Paging, required) The paging information for the request.
	 * @returns An Observable that emits the list of logs or an error.
	 * @throws Throws an error if an exception occurs during the API request.
	 */
	GETLogs(
		paging: Paging,
		location_id: string,
		since: string,
		until?: string,
		mac_address?: string,
		username?: string,
	) {
		try {
			return this._httpUtils.getHTTPHeadersAsyncTenantLocation().pipe(
				mergeMap((httpHeaders) => {
					return this._http
						.get(
							`${this._url}/logs${paging.getQs()}&location_id=${location_id}&since=${since}${until ? `&until=${until}` : ''}${mac_address ? `&mac_address=${mac_address}` : ''}${username ? `&username=${username}` : ''}`,
							{ headers: httpHeaders },
						)
						.pipe(
							map(
								(res: any) => {
									const jsonConvert: JsonConvert = new JsonConvert();
									const response: LogListData = jsonConvert.deserializeObject(
										res,
										LogListData,
									);
									if (response.success) {
										return response;
									} else {
										this._httpUtils.handleError(response);
										return;
									}
								},
								catchError((err) => {
									if (err.status === 401) {
										// Unauthorized
										this._httpUtils.logout();
									}
									throw err;
								}),
							),
						);
				}),
				catchError((err) => {
					throw err;
				}),
			);
		} catch (error) {
			throw error;
		}
	}
}
